import { useState } from 'react'
import { Plus } from 'lucide-react'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import { useAppStore } from '@/store/useAppStore'
import {
  useAttachCardMutation,
  useDeletePaymentMethodMutation,
  usePaymentMethodsQuery,
  useSetMainPaymentMethodMutation,
  useStartPayment,
  type PaymentMethodResponse,
  type PaymentProvider,
} from '@features/payments'
import tributeLogo from '@/assets/images/payments/tribute.png'
import robokassaLogo from '@/assets/images/payments/robokassa.png'
import visaLogo from '@/assets/images/payments/visa.png'
import mirLogo from '@/assets/images/payments/mir.png'
import { DeletePaymentMethodConfirmModal } from './DeletePaymentMethodConfirmModal'
import { ManagePaymentMethodsModal } from './ManagePaymentMethodsModal'
import styles from './PaymentMethodsBlock.module.css'

const PROVIDER_LABEL: Record<PaymentProvider, string> = {
  robokassa: 'Робокасса',
  tribute: 'Tribute',
}

const PROVIDER_LOGO: Record<PaymentProvider, string> = {
  robokassa: robokassaLogo,
  tribute: tributeLogo,
}

const getCardBrandLogo = (brand: string | null): string | null => {
  if (!brand) return null
  const normalized = brand.toLowerCase()
  if (normalized.includes('visa')) return visaLogo
  if (normalized.includes('mir') || normalized.includes('мир')) return mirLogo
  return null
}

const getMaskedTail = (masked: string): string => {
  const digits = masked.replace(/\D/g, '')
  const tail = digits.slice(-4)
  return tail ? `**** **** **** ${tail}` : masked
}

export const PaymentMethodsBlock = () => {
  const subscriptionProvider = useAppStore(s => s.subscription.provider)
  // Подпись провайдера в шапке блока — показываем, через кого юзер платит сейчас.
  // Для подписок без провайдера (legacy) — подписи нет.
  const providerNote =
    subscriptionProvider === 'tribute'
      ? 'Вы платите через Tribute. Управление автопродлением в Tribute'
      : subscriptionProvider === 'robokassa'
        ? 'Вы платите через Robokassa'
        : null
  // Tribute не управляется через наши payment-methods — карты и кнопки скрываем,
  // оставляем только подпись о провайдере.
  const isTribute = subscriptionProvider === 'tribute'

  const { data, isLoading, error } = usePaymentMethodsQuery(!isTribute)
  const platform = usePlatform()
  const startPayment = useStartPayment()
  const attachMutation = useAttachCardMutation()
  const deleteMutation = useDeletePaymentMethodMutation()
  const setMainMutation = useSetMainPaymentMethodMutation()

  const methods = data?.methods ?? []
  // Текущий способ оплаты — основной (тот, что идёт на автопродление),
  // либо первый привязанный, если основной не помечен.
  const primaryMethod =
    methods.find(m => m.is_main) ?? methods[0] ?? null

  const [methodToDelete, setMethodToDelete] =
    useState<PaymentMethodResponse | null>(null)
  const deleteError = deleteMutation.isError
    ? 'Не удалось удалить карту. Попробуйте ещё раз.'
    : null
  const [isManageOpen, setManageOpen] = useState(false)

  const handleAddCard = () => {
    // Окно открываем синхронно по клику — после await mutate браузер блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    attachMutation.mutate(
      { provider: 'robokassa' },
      {
        onSuccess: invoice => startPayment(invoice, tab),
        onError: () => tab.abort(),
      }
    )
  }

  const handleDeleteRequest = (method: PaymentMethodResponse) => {
    deleteMutation.reset()
    setMethodToDelete(method)
  }

  const handleDeleteConfirm = () => {
    if (!methodToDelete) return
    deleteMutation.mutate(methodToDelete.payment_method_id, {
      onSuccess: () => setMethodToDelete(null),
    })
  }

  const handleDeleteClose = () => {
    if (deleteMutation.isLoading) return
    deleteMutation.reset()
    setMethodToDelete(null)
  }

  const handleSetMain = (method: PaymentMethodResponse) => {
    if (method.is_main || setMainMutation.isLoading) return
    setMainMutation.mutate(method.payment_method_id)
  }

  const brandLogo = primaryMethod
    ? getCardBrandLogo(primaryMethod.card_brand)
    : null

  if (isTribute) {
    return (
      <div className={styles.block}>
        <h3 className={styles.title}>Способ оплаты</h3>
        {providerNote && <p className={styles.providerNote}>{providerNote}</p>}
      </div>
    )
  }

  return (
    <div className={styles.block}>
      <h3 className={styles.title}>Способ оплаты</h3>
      {providerNote && <p className={styles.providerNote}>{providerNote}</p>}

      {Boolean(error) && (
        <p className={styles.error}>
          Не удалось загрузить способы оплаты. Попробуйте позже.
        </p>
      )}

      {isLoading && !primaryMethod && (
        <p className={styles.empty}>Загружаем способы оплаты…</p>
      )}

      {!isLoading && !primaryMethod && !error && (
        <button
          type="button"
          className={styles.attachCard}
          onClick={handleAddCard}
          disabled={attachMutation.isLoading}
        >
          <span className={styles.addPlus} aria-hidden>
            <Plus size={12} strokeWidth={2} color="#7AA9C7" />
          </span>
          <span className={styles.attachLabel}>
            {attachMutation.isLoading
              ? 'Открываем оплату…'
              : 'Привязать способ оплаты'}
          </span>
        </button>
      )}

      {primaryMethod && (
        <div className={styles.card}>
          <img
            src={PROVIDER_LOGO[primaryMethod.provider]}
            alt={PROVIDER_LABEL[primaryMethod.provider]}
            className={styles.providerLogo}
          />
          <span className={styles.maskedNumber}>
            {getMaskedTail(primaryMethod.masked_card_number)}
          </span>
          {brandLogo && (
            <img
              src={brandLogo}
              alt={primaryMethod.card_brand ?? ''}
              className={styles.brandLogo}
            />
          )}
          <button
            type="button"
            className={styles.changeButton}
            onClick={() => setManageOpen(true)}
          >
            Изменить
          </button>
        </div>
      )}

      {/* История платежей — скрыта до реализации раздела.
      <button
        type="button"
        className={styles.historyLink}
        onClick={() => setHistoryOpen(true)}
      >
        История платежей
      </button>
      */}

      {isManageOpen && (
        <ManagePaymentMethodsModal
          methods={methods}
          isLoading={isLoading}
          isAddingCard={attachMutation.isLoading}
          isSettingMain={setMainMutation.isLoading}
          isDeleting={deleteMutation.isLoading}
          onSetMain={handleSetMain}
          onDeleteRequest={handleDeleteRequest}
          onAddCard={handleAddCard}
          onClose={() => setManageOpen(false)}
        />
      )}

      {methodToDelete && (
        <DeletePaymentMethodConfirmModal
          maskedCardNumber={methodToDelete.masked_card_number}
          isSubmitting={deleteMutation.isLoading}
          errorText={deleteError}
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteClose}
        />
      )}

      {/* Модалка истории платежей — скрыта вместе с кнопкой выше.
      {isHistoryOpen && (
        <Modal
          onClose={() => setHistoryOpen(false)}
          variant={historyModalVariant}
          showClose
        >
          <div className={styles.historyModalBody}>
            <h3 className={styles.historyModalTitle}>История платежей</h3>
            <p className={styles.historyModalText}>
              Раздел появится позже. Скоро здесь будет список всех ваших
              платежей.
            </p>
            <Button onClick={() => setHistoryOpen(false)} block>
              Понятно
            </Button>
          </div>
        </Modal>
      )}
      */}
    </div>
  )
}
